import { type BenefitPlanGroup } from "@platform/btx-plan-designer";
import {
  isUnmappedProposalGroup,
  matchesLocProductType,
  matchesProposalCoverageType,
  UNMAPPED_PAGE_ID,
} from "@platform/mpx-quotes-grid";
import { BenefitPlanTypeEnum } from "@platform/openapi-types";
import { CoverageTypeEnum } from "@platform/types";
import { useMemo } from "react";

const UNMAPPED_PAGE_NICKNAME = "Unmapped Plans";

interface ProposalGroupLike {
  rfpBenefitPlanGroupId?: string;
  isPlaceholder?: boolean;
  productType?: string;
}

interface ProposalCoverageLike {
  coverageType: string;
  planGroups: ProposalGroupLike[];
}

interface ProposalLike {
  carrierName: string;
  coverages: ProposalCoverageLike[];
}

interface UnmappedByCoverage {
  countByCoverage: Partial<Record<CoverageTypeEnum, number>>;
  carrierNames: string[];
}

/**
 * Counts real unmapped carrier plan groups per coverage type, and the
 * distinct carrier names responsible for them (for the info banner).
 */
const countUnmappedByCoverage = (
  proposals: ProposalLike[],
  coverageTypes: CoverageTypeEnum[],
): UnmappedByCoverage => {
  const countByCoverage: Partial<Record<CoverageTypeEnum, number>> = {};
  const originalCarrierNames = new Set<string>();

  for (const coverageType of coverageTypes) {
    let count = 0;
    for (const proposal of proposals) {
      for (const coverage of proposal.coverages) {
        if (!matchesProposalCoverageType(coverage.coverageType, coverageType))
          continue;

        const unmapped = coverage.planGroups
          .filter((group) =>
            matchesLocProductType(group.productType, coverageType),
          )
          .filter(isUnmappedProposalGroup);
        count += unmapped.length;
        if (unmapped.length > 0) originalCarrierNames.add(proposal.carrierName);
      }
    }
    if (count > 0) countByCoverage[coverageType] = count;
  }
  const carrierNames = [...originalCarrierNames].sort((a, b) =>
    a.localeCompare(b),
  );

  return { countByCoverage, carrierNames };
};

const buildUnmappedPageEntry = (position: number): BenefitPlanGroup => ({
  id: UNMAPPED_PAGE_ID,
  createdAt: "",
  updatedAt: "",
  position,
  productType: BenefitPlanTypeEnum.notApplicable,
  nickname: UNMAPPED_PAGE_NICKNAME,
  size: 0,
  benefitPlans: [],
});

interface UseUnmappedPlansPagesResult {
  /**
   * `benefitPlanGroupsByCoverage` with a synthetic "Unmapped Plans" entry
   * appended for each coverage that has real unmapped carrier groups.
   */
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >;
  /** Count of unmapped carrier groups per coverage, for the LOC picker's badges. */
  unmappedCountByCoverage: Partial<Record<CoverageTypeEnum, number>>;
  /** Distinct carrier names with unmapped plans across all coverages, for the banner. */
  carrierNamesWithUnmappedPlans: string[];
}

/**
 * Injects the synthetic "Unmapped Plans" SBS page into the per-coverage
 * benefit plan group lists, and surfaces the counts the LOC picker badges
 * and info banner need.
 */
export const useUnmappedPlansPages = (
  proposals: ProposalLike[],
  benefitPlanGroupsByCoverage: Partial<
    Record<CoverageTypeEnum, BenefitPlanGroup[]>
  >,
  availableCoverageTypes: CoverageTypeEnum[],
): UseUnmappedPlansPagesResult =>
  useMemo(() => {
    const allCoverageTypes = Object.keys(
      benefitPlanGroupsByCoverage,
    ) as CoverageTypeEnum[];

    // Coverages that only have Alternate-period benefit plan groups are
    // excluded from the coverage-summary endpoint, so they never appear in
    // availableCoverageTypes or the CoverageBenefitPlanSelector menu. Counting
    // their unmapped plans anyway would surface a banner the broker has no
    // way to act on, since there's no menu entry to click through to them.
    const availableCoverageTypeSet = new Set(availableCoverageTypes);
    const countableCoverageTypes = allCoverageTypes.filter((coverageType) =>
      availableCoverageTypeSet.has(coverageType),
    );
    const { countByCoverage, carrierNames } = countUnmappedByCoverage(
      proposals,
      countableCoverageTypes,
    );

    const enriched: Partial<Record<CoverageTypeEnum, BenefitPlanGroup[]>> = {};
    for (const coverageType of allCoverageTypes) {
      const groups = benefitPlanGroupsByCoverage[coverageType] ?? [];
      enriched[coverageType] = countByCoverage[coverageType]
        ? [...groups, buildUnmappedPageEntry(groups.length + 1)]
        : groups;
    }

    return {
      benefitPlanGroupsByCoverage: enriched,
      unmappedCountByCoverage: countByCoverage,
      carrierNamesWithUnmappedPlans: carrierNames,
    };
  }, [proposals, benefitPlanGroupsByCoverage, availableCoverageTypes]);
